import { useEffect, useState } from "react";
import { Search, Plus, Trash2, Pencil, X } from "lucide-react";
import { api } from "../lib/apiClient";
import { useApp } from "../lib/store";
import { PageHeader, GradeBadge, Avatar, Empty, ScoreBar } from "../components/Shared";
import { toast } from "sonner";

const DEPTS = ["Media", "Engineering", "Marketing", "Design", "Operations"];

export default function Employees() {
  const { month, year, can } = useApp();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [dept, setDept] = useState("all");
  const [grade, setGrade] = useState("all");
  const [sort, setSort] = useState("rank");
  const [modal, setModal] = useState(null);

  const load = () => api.performance(month, year).then(setRows).catch(() => {});
  useEffect(() => { load(); }, [month, year]);

  let filtered = rows.filter((r) =>
    r.name.toLowerCase().includes(search.toLowerCase()) &&
    (dept === "all" || r.department === dept) &&
    (grade === "all" || r.grade === grade));
  filtered = [...filtered].sort((a, b) => {
    if (sort === "rank") return a.rank - b.rank;
    if (sort === "name") return a.name.localeCompare(b.name);
    if (sort === "increment") return b.recommended_increment - a.recommended_increment;
    return (b.month_score || 0) - (a.month_score || 0);
  });

  return (
    <div>
      <PageHeader title="Employee Directory" subtitle="Team roster with live performance, grade and increment eligibility"
        exportModule="performance" exportParams={{ month, year, department: dept }}>
        {can.manageEmployees && (
          <button data-testid="add-employee-button" onClick={() => setModal({ status: "Active", department: "Media" })}
            className="pill px-3 py-2 text-sm font-semibold brand-bg text-white hover:opacity-90 transition-opacity">
            <Plus size={15} /> Add Employee
          </button>
        )}
      </PageHeader>

      <div className="card-panel p-4 mb-5 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-c" />
          <input data-testid="employee-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name…" className="input-c pl-9" />
        </div>
        <select data-testid="department-filter-select" value={dept} onChange={(e) => setDept(e.target.value)} className="input-c" style={{ maxWidth: 180 }}>
          <option value="all">All Departments</option>
          {DEPTS.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select data-testid="grade-filter-select" value={grade} onChange={(e) => setGrade(e.target.value)} className="input-c" style={{ maxWidth: 150 }}>
          <option value="all">All Grades</option>
          {["A+", "A", "B+", "B", "C+", "C", "D"].map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="input-c" style={{ maxWidth: 160 }}>
          <option value="rank">Sort: Rank</option>
          <option value="score">Sort: Score</option>
          <option value="name">Sort: Name</option>
          <option value="increment">Sort: Increment</option>
        </select>
      </div>

      <div className="card-panel overflow-x-auto" data-testid="employee-table">
        <table className="data-table">
          <thead>
            <tr>
              <th>Rank</th><th>Employee</th><th>Designation</th><th>Department</th>
              <th>Month Score</th><th>Quarter</th><th>Year</th><th>Grade</th><th>Increment</th>
              {can.manageEmployees && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.employee_id} data-testid={`employee-row-${r.employee_id}`}>
                <td className="mono font-bold text-muted-c">#{r.rank}</td>
                <td>
                  <div className="flex items-center gap-2.5">
                    <Avatar src={r.profile_image} name={r.name} size={34} />
                    <span className="font-medium">{r.name}</span>
                  </div>
                </td>
                <td className="text-secondary">{r.designation}</td>
                <td><span className="pill px-2.5 py-0.5 border-subtle text-secondary text-xs">{r.department}</span></td>
                <td style={{ minWidth: 150 }}>{r.month_score != null ? <ScoreBar value={r.month_score} /> : <span className="text-muted-c text-xs">No review</span>}</td>
                <td className="mono text-sm">{r.quarter_score}</td>
                <td className="mono text-sm">{r.year_score}</td>
                <td><GradeBadge grade={r.grade} /></td>
                <td className="mono font-semibold brand-text">{r.recommended_increment}%</td>
                {can.manageEmployees && (
                  <td>
                    <div className="flex gap-1.5">
                      <button onClick={() => api.employee(r.employee_id).then(setModal)} className="w-8 h-8 rounded-lg elevated border border-subtle flex items-center justify-center hover:brand-text transition-colors" data-testid={`edit-${r.employee_id}`}><Pencil size={14} /></button>
                      <button onClick={() => { if (window.confirm(`Delete ${r.name}?`)) api.deleteEmployee(r.employee_id).then(() => { toast.success("Employee deleted"); load(); }); }} className="w-8 h-8 rounded-lg elevated border border-subtle flex items-center justify-center hover:text-red-500 transition-colors" data-testid={`delete-${r.employee_id}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {!filtered.length && <Empty text="No employees match filters" />}
      </div>

      {modal && <EmployeeModal emp={modal} seeSalary={can.seeSalary} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

function EmployeeModal({ emp, onClose, onSaved, seeSalary }) {
  const [form, setForm] = useState({
    name: emp.name || "", designation: emp.designation || "", department: emp.department || "Media",
    joining_date: emp.joining_date || "", current_salary: emp.current_salary || 0, status: emp.status || "Active",
    profile_image: emp.profile_image || "",
  });
  const isEdit = !!emp.id;
  const set = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    if (!form.name || !form.designation) return toast.error("Name and designation required");
    try {
      if (isEdit) await api.updateEmployee(emp.id, form);
      else await api.createEmployee(form);
      toast.success(isEdit ? "Employee updated" : "Employee added");
      onSaved();
    } catch { toast.error("Save failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose}>
      <div className="card-panel w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="employee-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-subtle">
          <h3 className="font-display font-semibold">{isEdit ? "Edit Employee" : "Add Employee"}</h3>
          <button onClick={onClose} className="text-muted-c hover:text-red-500"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <Field label="Full Name"><input className="input-c" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="emp-name-input" /></Field>
          <Field label="Designation"><input className="input-c" value={form.designation} onChange={(e) => set("designation", e.target.value)} data-testid="emp-designation-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Department">
              <select className="input-c" value={form.department} onChange={(e) => set("department", e.target.value)}>
                {DEPTS.map((d) => <option key={d}>{d}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select className="input-c" value={form.status} onChange={(e) => set("status", e.target.value)}>
                <option>Active</option><option>Inactive</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Joining Date"><input type="date" className="input-c" value={(form.joining_date || "").slice(0, 10)} onChange={(e) => set("joining_date", e.target.value)} /></Field>
            {seeSalary && <Field label="Current Salary"><input type="number" className="input-c" value={form.current_salary} onChange={(e) => set("current_salary", +e.target.value)} /></Field>}
          </div>
          <Field label="Profile Image URL"><input className="input-c" value={form.profile_image} onChange={(e) => set("profile_image", e.target.value)} placeholder="https://…" /></Field>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-subtle">
          <button onClick={onClose} className="pill px-4 py-2 text-sm border-strong">Cancel</button>
          <button onClick={save} className="pill px-4 py-2 text-sm brand-bg text-white font-semibold" data-testid="save-employee-button">Save</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="text-xs font-semibold text-muted-c uppercase tracking-wider block mb-1.5">{label}</label>{children}</div>;
}
